import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ── Task Type Config ──
const TASK_CONFIG = {
  RECOMMENDATION: { label: "Recommendation", color: "emerald", icon: "R" },
  APPROVAL_PENDING: { label: "Approval", color: "blue", icon: "A" },
  SCRIPT_CHANGES_REQUESTED: { label: "Revise Script", color: "amber", icon: "S" },
  PRODUCTION_IN_PROGRESS: { label: "Production", color: "purple", icon: "P" },
  CAMPAIGN_ALERT: { label: "Campaign Alert", color: "red", icon: "!" },
  FOUNDER_ASSIGNMENT: { label: "Assignment", color: "cyan", icon: "F" },
};

const colorMap = {
  emerald: { bg: "bg-emerald-500/15", border: "border-emerald-500/30", text: "text-emerald-400", dot: "bg-emerald-500" },
  blue: { bg: "bg-blue-500/15", border: "border-blue-500/30", text: "text-blue-400", dot: "bg-blue-500" },
  amber: { bg: "bg-amber-500/15", border: "border-amber-500/30", text: "text-amber-400", dot: "bg-amber-500" },
  purple: { bg: "bg-purple-500/15", border: "border-purple-500/30", text: "text-purple-400", dot: "bg-purple-500" },
  red: { bg: "bg-red-500/15", border: "border-red-500/30", text: "text-red-400", dot: "bg-red-500" },
  cyan: { bg: "bg-cyan-500/15", border: "border-cyan-500/30", text: "text-cyan-400", dot: "bg-cyan-500" },
};

// ── Single Task Card ──
const TaskCard = ({ task, onComplete, completing }) => {
  const cfg = TASK_CONFIG[task.task_type] || { label: task.task_type, color: "emerald", icon: "?" };
  const colors = colorMap[cfg.color] || colorMap.emerald;

  return (
    <div
      data-testid={`task-card-${task.task_id}`}
      className={`bg-slate-900/60 border border-slate-800 rounded-xl p-4 hover:border-slate-700 transition-all group ${
        task.delay_note ? "ring-1 ring-amber-500/20" : ""
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Rank + Type Icon */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <span className="text-xs font-mono text-slate-600">#{task.rank}</span>
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold ${colors.bg} ${colors.text}`}
          >
            {cfg.icon}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors.bg} ${colors.text} ${colors.border} border`}>
              {cfg.label}
            </span>
            {task.recommendation_type && (
              <span className="text-xs text-slate-500 font-medium">{task.recommendation_type}</span>
            )}
            {task.delay_note && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
                {task.delay_note}
              </span>
            )}
          </div>

          <p className="text-sm text-white font-medium leading-snug truncate" title={task.title}>
            {task.title}
          </p>

          <p className="text-xs text-slate-400">{task.action}</p>

          <div className="flex items-center gap-4 text-xs text-slate-500">
            {task.time_to_action && <span>Window: {task.time_to_action}</span>}
            {task.impact_score > 0 && (
              <span>
                Impact: <span className="text-emerald-400 font-medium">{task.impact_score.toFixed(1)}</span>
              </span>
            )}
            {task.urgency > 1.2 && (
              <span className="text-red-400 font-medium">URGENT</span>
            )}
          </div>
        </div>

        {/* Complete Button */}
        <button
          onClick={() => onComplete(task.task_id, task.task_type)}
          disabled={completing === task.task_id}
          data-testid={`complete-task-${task.task_id}`}
          className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-slate-700 flex items-center justify-center text-slate-600 hover:border-emerald-500 hover:text-emerald-500 transition-colors group-hover:border-slate-600 disabled:opacity-50"
          title="Mark complete"
        >
          {completing === task.task_id ? (
            <div className="w-3 h-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

// ── Summary Stats Row ──
const SummaryStats = ({ summary }) => {
  if (!summary) return null;
  const items = [
    { label: "Recommendations", value: summary.recommendations, color: "text-emerald-400" },
    { label: "Approvals", value: summary.approvals, color: "text-blue-400" },
    { label: "Script Revisions", value: summary.script_revisions, color: "text-amber-400" },
    { label: "Productions", value: summary.productions, color: "text-purple-400" },
    { label: "Alerts", value: summary.campaign_alerts, color: "text-red-400" },
    { label: "Assignments", value: summary.assignments, color: "text-cyan-400" },
  ].filter((i) => i.value > 0);

  return (
    <div className="flex flex-wrap gap-3" data-testid="my-day-summary-stats">
      {items.map((item) => (
        <div key={item.label} className="bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2 flex items-center gap-2">
          <span className={`text-lg font-bold ${item.color}`}>{item.value}</span>
          <span className="text-xs text-slate-500">{item.label}</span>
        </div>
      ))}
      {summary.carryover > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 flex items-center gap-2">
          <span className="text-lg font-bold text-amber-400">{summary.carryover}</span>
          <span className="text-xs text-amber-500">Carried Over</span>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN MY DAY PAGE
// ============================================================================
const MyDayPage = () => {
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [filterType, setFilterType] = useState("ALL");

  const fetchQueue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/my-day`);
      setQueue(res.data);
    } catch (err) {
      console.error("Failed to fetch My Day queue:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const handleComplete = async (taskId, taskType) => {
    setCompleting(taskId);
    try {
      await axios.post(`${API}/my-day/complete`, { task_id: taskId, task_type: taskType });
      // Remove from local state immediately
      setQueue((prev) => ({
        ...prev,
        total_tasks: prev.total_tasks - 1,
        tasks: prev.tasks.filter((t) => t.task_id !== taskId),
      }));
    } catch (err) {
      alert("Failed to complete task: " + (err.response?.data?.detail || err.message));
    }
    setCompleting(null);
  };

  const tasks = queue?.tasks || [];
  const filteredTasks = filterType === "ALL" ? tasks : tasks.filter((t) => t.task_type === filterType);

  // Get unique task types for filter
  const taskTypes = [...new Set(tasks.map((t) => t.task_type))];

  return (
    <div className="space-y-6" data-testid="my-day-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white" data-testid="my-day-title">My Day</h2>
        <p className="text-slate-400 text-sm mt-1">
          {queue?.date || "Today"} — {queue?.total_tasks || 0} tasks ranked by priority
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {/* Summary */}
          <SummaryStats summary={queue?.summary} />

          {/* Filters */}
          {taskTypes.length > 1 && (
            <div className="flex gap-1 bg-slate-900/60 border border-slate-800 rounded-lg p-1 w-fit">
              <button
                onClick={() => setFilterType("ALL")}
                data-testid="my-day-filter-all"
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  filterType === "ALL" ? "bg-emerald-500/20 text-emerald-400" : "text-slate-400 hover:text-white"
                }`}
              >
                All ({tasks.length})
              </button>
              {taskTypes.map((tt) => {
                const cfg = TASK_CONFIG[tt] || { label: tt };
                const count = tasks.filter((t) => t.task_type === tt).length;
                return (
                  <button
                    key={tt}
                    onClick={() => setFilterType(tt)}
                    data-testid={`my-day-filter-${tt.toLowerCase()}`}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      filterType === tt ? "bg-emerald-500/20 text-emerald-400" : "text-slate-400 hover:text-white"
                    }`}
                  >
                    {cfg.label} ({count})
                  </button>
                );
              })}
            </div>
          )}

          {/* Task Queue */}
          {filteredTasks.length === 0 ? (
            <div className="text-center py-16 bg-slate-900/40 border border-slate-800 rounded-xl" data-testid="my-day-empty">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-slate-400 text-sm font-medium">All clear</p>
              <p className="text-slate-600 text-xs mt-1">No tasks in your queue right now.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTasks.map((task) => (
                <TaskCard
                  key={task.task_id}
                  task={task}
                  onComplete={handleComplete}
                  completing={completing}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyDayPage;
